// ---------- reconstruire une vignette qu'on n'a pas ----------
//
// LA VIGNETTE NE VOYAGE PAS, et c'est délibéré : c'est quelques dizaines de kilo-octets de
// pixels entièrement reconstructibles, qui écraseraient la sauvegarde qu'ils illustrent (cf. la
// déclaration de `thumb` dans src/progress.ts). Les taps, eux, se synchronisent.
//
// Le prix de ce choix se paie à la RÉINSTALLATION. Le code de récupération ramène la progression
// par l'index du nuage — `mergeRemoteIndex()` écrit `pct`, `done` et la date, jamais l'image —,
// si bien qu'un joueur retrouvait ses puzzles terminés mais pas leurs dessins : un flux de
// cartes anonymes à la place de ce qu'il avait peint. C'est ce que ce fichier répare.
//
// POURQUOI CE MODULE EXISTE plutôt qu'une fonction dans l'écran qui en a besoin : la
// reconstruction était née dans src/daily.ts, pour le calendrier, et le flux ne l'a jamais eue.
// Le même défaut a donc vécu des mois sur l'écran d'accueil pendant qu'il était corrigé sur le
// calendrier. Une seule implémentation, deux appelants, et le prochain écran n'aura pas à
// redécouvrir ni la route exempte de pack, ni le raccourci des feuilles finies.

import { fetchSheet, isCreation } from './api';
import { getCreation } from './creationStore';
import { INTRO_ID, introSheet } from './introGrid';
import { loadProgress, setThumb, targetFingerprint } from './progress';
import { renderThumbOf } from './render';

/**
 * Reconstruit la vignette de `id` et l'écrit en local. Rend l'image, ou `null` s'il n'y avait
 * rien à dessiner.
 *
 * LE RACCOURCI DES FEUILLES FINIES est ce qui rend la réparation possible après une
 * réinstallation. Une feuille TERMINÉE est entièrement peinte : son image ne dépend plus de ce
 * que le joueur a fait, seulement de la grille. On n'a donc besoin ni des taps, ni de les
 * attendre du nuage — ce qui tombe bien, puisque l'index qui vient d'être fusionné ne les porte
 * pas (les enregistrements sont `stale`, leur liste est vide jusqu'à la première ouverture). Le
 * cas que le joueur remarque est exactement celui qui se répare sans réseau supplémentaire.
 *
 * Une feuille EN COURS, elle, demande ses taps, et on ne reconstruit que si on les a déjà :
 * dessiner à partir d'une liste qu'on sait incomplète afficherait un dessin moins avancé que ce
 * que le joueur a fait — un mensonge dans le sens qui décourage. Sa carte garde son glyphe
 * jusqu'à ce qu'elle soit ouverte, ce qui rapatrie la sauvegarde complète.
 *
 * NE LÈVE JAMAIS : l'appelant en enchaîne des dizaines, et une image manquante n'est pas une
 * panne dont il y ait quelque chose à dire au joueur.
 */
export async function rebuildThumb(id: string, done: boolean): Promise<string | null> {
  try {
    // Une CRÉATION se lit sur le téléphone, le seul endroit où elle existe depuis le 2026-10-30
    // (src/creationStore.ts). Absente, il n'y a rien à redessiner.
    // Le chien de l'initiation n'est servi à personne (il est retiré, cf. introGrid.ts) : le lui
    // demander coûtait un 404, et une lecture de base, à chaque passage au catalogue tant que
    // sa pièce — qui n'enregistre pas de vignette — était en cours.
    const sheet = isCreation(id)
      ? await getCreation(id)
      : id === INTRO_ID
        ? introSheet()
        : await fetchSheet(id);
    if (!sheet) return null;
    const fp = targetFingerprint(sheet.target);
    let filled: (r: number, c: number) => boolean;
    if (done) {
      filled = () => true;
    } else {
      const saved = loadProgress(id, fp);
      if (!saved?.taps.length) return null;
      const painted = new Set(saved.taps);
      filled = (r, c) => painted.has(r * sheet.gridW + c);
    }
    const url = renderThumbOf({
      gridW: sheet.gridW,
      gridH: sheet.gridH,
      target: sheet.target,
      alphabetId: sheet.alphabetId,
      filled,
    });
    if (!url) return null;
    // Écrite en local : la réparation ne se paie qu'UNE FOIS par appareil et par feuille, et
    // l'ouverture suivante de l'écran n'a plus rien à télécharger.
    setThumb(id, fp, url);
    return url;
  } catch {
    return null;
  }
}
